// src/hooks/useInsurancePersistence.ts

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  INSURANCE_STORAGE_KEYS,
  loadInsuranceData,
  saveInsuranceData,
} from "../services/insurance-storage.service";

import {
  SAMPLE_CLAIMS,
  SAMPLE_ELIGIBILITY_VERIFICATIONS,
  SAMPLE_POLICIES,
  SAMPLE_PROVIDERS,
  SAMPLE_REPORTS,
} from "../mocks/insurance.mock-data";

import type {
  ActivityLog,
  Authorization,
  Claim,
  EligibilityVerification,
  Enrollment,
  Policy,
  Provider,
  Report,
} from "../contexts/InsuranceContextType";

interface InsuranceState {
  claims: Claim[];
  policies: Policy[];
  enrollments: Enrollment[];
  authorizations: Authorization[];
  providers: Provider[];
  activityLogs: ActivityLog[];
  eligibilityVerifications: EligibilityVerification[];
  reports: Report[];
}

interface InsuranceStateSetters {
  setClaims: React.Dispatch<
    React.SetStateAction<Claim[]>
  >;

  setPolicies: React.Dispatch<
    React.SetStateAction<Policy[]>
  >;

  setEnrollments: React.Dispatch<
    React.SetStateAction<Enrollment[]>
  >;

  setAuthorizations: React.Dispatch<
    React.SetStateAction<Authorization[]>
  >;

  setProviders: React.Dispatch<
    React.SetStateAction<Provider[]>
  >;

  setActivityLogs: React.Dispatch<
    React.SetStateAction<ActivityLog[]>
  >;

  setEligibilityVerifications: React.Dispatch<
    React.SetStateAction<EligibilityVerification[]>
  >;

  setReports: React.Dispatch<
    React.SetStateAction<Report[]>
  >;
}

interface UseInsurancePersistenceOptions
  extends InsuranceState,
    InsuranceStateSetters {}

interface UseInsurancePersistenceReturn {
  isLoading: boolean;
  refreshData: () => Promise<void>;
}

function loadStoredInsuranceState(): InsuranceState {
  return {
    claims: loadInsuranceData(
      INSURANCE_STORAGE_KEYS.CLAIMS,
      SAMPLE_CLAIMS
    ),

    policies: loadInsuranceData(
      INSURANCE_STORAGE_KEYS.POLICIES,
      SAMPLE_POLICIES
    ),

    enrollments:
      loadInsuranceData<Enrollment[]>(
        INSURANCE_STORAGE_KEYS.ENROLLMENTS,
        []
      ),

    authorizations:
      loadInsuranceData<Authorization[]>(
        INSURANCE_STORAGE_KEYS.AUTHORIZATIONS,
        []
      ),

    providers: loadInsuranceData(
      INSURANCE_STORAGE_KEYS.PROVIDERS,
      SAMPLE_PROVIDERS
    ),

    activityLogs:
      loadInsuranceData<ActivityLog[]>(
        INSURANCE_STORAGE_KEYS.ACTIVITY_LOGS,
        []
      ),

    eligibilityVerifications:
      loadInsuranceData(
        INSURANCE_STORAGE_KEYS
          .ELIGIBILITY_VERIFICATIONS,
        SAMPLE_ELIGIBILITY_VERIFICATIONS
      ),

    reports: loadInsuranceData(
      INSURANCE_STORAGE_KEYS.REPORTS,
      SAMPLE_REPORTS
    ),
  };
}

export function useInsurancePersistence({
  claims,
  policies,
  enrollments,
  authorizations,
  providers,
  activityLogs,
  eligibilityVerifications,
  reports,

  setClaims,
  setPolicies,
  setEnrollments,
  setAuthorizations,
  setProviders,
  setActivityLogs,
  setEligibilityVerifications,
  setReports,
}: UseInsurancePersistenceOptions): UseInsurancePersistenceReturn {
  const [isLoading, setIsLoading] =
    useState(true);

  const applyStoredState = useCallback(() => {
    const storedState =
      loadStoredInsuranceState();

    setClaims(storedState.claims);
    setPolicies(storedState.policies);
    setEnrollments(
      storedState.enrollments
    );
    setAuthorizations(
      storedState.authorizations
    );
    setProviders(storedState.providers);
    setActivityLogs(
      storedState.activityLogs
    );
    setEligibilityVerifications(
      storedState.eligibilityVerifications
    );
    setReports(storedState.reports);
  }, [
    setClaims,
    setPolicies,
    setEnrollments,
    setAuthorizations,
    setProviders,
    setActivityLogs,
    setEligibilityVerifications,
    setReports,
  ]);

  useEffect(() => {
    applyStoredState();
    setIsLoading(false);
  }, [applyStoredState]);

  useEffect(() => {
    if (isLoading) return;

    saveInsuranceData(
      INSURANCE_STORAGE_KEYS.CLAIMS,
      claims
    );
  }, [claims, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    saveInsuranceData(
      INSURANCE_STORAGE_KEYS.POLICIES,
      policies
    );
  }, [policies, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    saveInsuranceData(
      INSURANCE_STORAGE_KEYS.ENROLLMENTS,
      enrollments
    );
  }, [enrollments, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    saveInsuranceData(
      INSURANCE_STORAGE_KEYS.AUTHORIZATIONS,
      authorizations
    );
  }, [authorizations, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    saveInsuranceData(
      INSURANCE_STORAGE_KEYS.PROVIDERS,
      providers
    );
  }, [providers, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    saveInsuranceData(
      INSURANCE_STORAGE_KEYS.ACTIVITY_LOGS,
      activityLogs
    );
  }, [activityLogs, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    saveInsuranceData(
      INSURANCE_STORAGE_KEYS
        .ELIGIBILITY_VERIFICATIONS,
      eligibilityVerifications
    );
  }, [
    eligibilityVerifications,
    isLoading,
  ]);

  useEffect(() => {
    if (isLoading) return;

    saveInsuranceData(
      INSURANCE_STORAGE_KEYS.REPORTS,
      reports
    );
  }, [reports, isLoading]);

  const refreshData = useCallback(
    async () => {
      applyStoredState();
    },
    [applyStoredState]
  );

  return {
    isLoading,
    refreshData,
  };
}